import { create } from 'zustand'
import { reduce, type Action, type Conversation } from '@/components/helpFeedback/conversation'

export type PanelTab = 'ask' | 'mine'

/** Whether the assistant answers: unknown until `/api/app-feedback/status` is read; read again each time the panel opens. */
export type AssistantStatus = 'unknown' | 'available' | 'unavailable'

/**
 * The Help & feedback panel (SA-1310): whether it is open, which tab, the one conversation (kept
 * while the panel is closed, so a reopen can offer to resume it), whether the mic was refused this
 * session, and the assistant's status. Emptied on every identity change
 * (`identityScope.RESET_TO_INITIAL`): what someone was telling us is theirs alone.
 */
interface HelpFeedbackState {
  open: boolean
  tab: PanelTab
  conversation: Conversation | null
  micHidden: boolean
  status: AssistantStatus
  /** The deployment's Help & feedback settings, from `/status` (SA-1306 §3); on until read. */
  answers: boolean
  voice: boolean
  /** Set when the panel opens on an unsent conversation holding the person's words. */
  offerResume: boolean
  /** What the composer holds, kept while the panel is closed; `spoken` when voice put words there. */
  draftText: string
  draftSpoken: boolean
  /** The words carried into the plain report when the assistant turns out not to be set up. */
  plainWords: string
  setOpen: (open: boolean) => void
  setTab: (tab: PanelTab) => void
  start: (conversation: Conversation) => void
  dispatch: (action: Action) => void
  hideMic: () => void
  setStatus: (status: AssistantStatus) => void
  setOffers: (offers: { answers: boolean; voice: boolean }) => void
  setOfferResume: (offer: boolean) => void
  setDraft: (text: string, spoken?: boolean) => void
  setPlainWords: (words: string) => void
  clear: () => void
}

export const useHelpFeedbackStore = create<HelpFeedbackState>((set, get) => ({
  open: false,
  tab: 'ask',
  conversation: null,
  micHidden: false,
  status: 'unknown',
  answers: true,
  voice: true,
  offerResume: false,
  draftText: '',
  draftSpoken: false,
  plainWords: '',
  setOpen: (open) => set({ open }),
  setTab: (tab) => set({ tab }),
  start: (conversation) => set({ conversation }),
  dispatch: (action) => {
    const c = get().conversation
    if (c) set({ conversation: reduce(c, action) })
  },
  hideMic: () => set({ micHidden: true }),
  setStatus: (status) => set({ status }),
  setOffers: ({ answers, voice }) => set({ answers, voice }),
  setOfferResume: (offerResume) => set({ offerResume }),
  setDraft: (draftText, spoken) => set((s) => ({ draftText, draftSpoken: draftText ? (spoken ?? s.draftSpoken) : false })),
  setPlainWords: (plainWords) => set({ plainWords }),
  clear: () => set({ conversation: null, offerResume: false }),
}))
